/**
 * The board's Total line (19/10/2026): the shown rows added up, column by
 * column — list groups and lists as the cells read them (one shared by two
 * containers counts once), drops as Progress reads them. "With a drop" =
 * at least one drop of today still goes to it (skipped or stopped ones don't).
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanContainerRow, DropGenContainerSummary } from '../../../drop-generator.types';
import { deriveContainerLive } from '../../live/live-status';
import { boardListRows } from '../board-list-rows';

export interface BoardTotals {
  containers: number;
  on: number;
  groups: number;
  groupsWithDrops: number;
  lists: number;
  listsWithDrops: number;
  /** Drops of the day — the Progress column's "planned" (sent ones included). */
  planned: number;
  sent: number;
  /** At least one row's day is generated — the "sent" count means something. */
  generated: boolean;
}

const EMPTY: BoardTotals = { containers: 0, on: 0, groups: 0, groupsWithDrops: 0, lists: 0, listsWithDrops: 0, planned: 0, sent: 0, generated: false };

function addPreview(totals: BoardTotals, preview: DayPlanContainerRow, seen: { groups: Map<string, boolean>; lists: Map<number, boolean> }): BoardTotals {
  for (const group of boardListRows(preview)) {
    const live = group.todayOrder.some((list) => list.sends > 0);
    seen.groups.set(group.listGroupId, Boolean(seen.groups.get(group.listGroupId)) || live);
    for (const list of group.todayOrder) seen.lists.set(list.wiserId, Boolean(seen.lists.get(list.wiserId)) || list.sends > 0);
  }
  const facts = deriveContainerLive(preview);
  const generated = facts.state !== 'not-generated';
  return {
    ...totals,
    planned: totals.planned + (generated ? facts.planned : preview.totals.drops),
    sent: totals.sent + facts.sent,
    generated: totals.generated || generated,
  };
}

const countTrue = (seen: Map<unknown, boolean>) => [...seen.values()].filter(Boolean).length;

export function boardTotals(rows: DropGenContainerSummary[], previewOf: (id: string) => DayPlanContainerRow | undefined): BoardTotals {
  const seen = { groups: new Map<string, boolean>(), lists: new Map<number, boolean>() };
  let totals: BoardTotals = { ...EMPTY, containers: rows.length, on: rows.filter((c) => c.enabled).length };
  for (const row of rows) {
    const preview = previewOf(row.id);
    if (preview) totals = addPreview(totals, preview, seen);
  }
  return {
    ...totals,
    groups: seen.groups.size,
    groupsWithDrops: countTrue(seen.groups),
    lists: seen.lists.size,
    listsWithDrops: countTrue(seen.lists),
  };
}
